import { useCallback, useState, type ReactNode } from "react";
import API from "@app/services/api";
import { ExportShareCapDialog } from "@app/components/ExportShareCapDialog";

/** What an export takes outside the reader's patientèle (PT-120), as ``/export/quota`` answers it. */
export interface ExportAllowance {
  capped: boolean;
  /** Whose counter the export adds to: the organisation worked in, or the account in none. */
  holder: "organisation" | "compte";
  /** Null when the colleagues' patients are fewer than the threshold: a handful is not counted out. */
  patients: number | null;
  outside: number | null;
  new: number | null;
  share: number;
  cap: number;
  allowed: boolean;
  contact: string;
}

/** ``detail.code`` of the 403 an export over the cap answers with. */
const REFUSED_CODE = "export_share_cap";

type ExportQuery = Record<string, string | number | boolean | null | undefined>;

function isAllowance(value: unknown): value is ExportAllowance {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as ExportAllowance).allowed === "boolean" &&
    typeof (value as ExportAllowance).share === "number"
  );
}

/** The allowance a refused export carries, or null when the failure is something else. */
export async function refusedAllowance(
  response: Response,
): Promise<ExportAllowance | null> {
  if (response.status !== 403) return null;
  try {
    const body: unknown = await response.json();
    const detail = (body as { detail?: unknown })?.detail;
    if (
      isAllowance(detail) &&
      (detail as unknown as { code?: string }).code === REFUSED_CODE
    ) {
      return detail;
    }
  } catch {
    return null;
  }
  return null;
}

interface Pending {
  allowance: ExportAllowance;
  resolve: (go: boolean) => void;
}

/**
 * Asks before an export that takes patients outside the reader's patientèle, and explains a refusal.
 * The server decides: a preview that fails lets the export through, and the export enforces the cap.
 */
export function useExportShareCap(): {
  confirm: (query: ExportQuery, onPrompt?: () => void) => Promise<boolean>;
  showRefusal: (allowance: ExportAllowance) => void;
  dialog: ReactNode;
} {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback(
    async (query: ExportQuery, onPrompt?: () => void) => {
      const res = await API.get({
        path: "/dashboard/pathway/export/quota",
        query,
      });
      const allowance: unknown = res?.ok ? res.data : null;
      if (
        !isAllowance(allowance) ||
        !allowance.capped ||
        allowance.outside === 0
      ) {
        return true;
      }
      onPrompt?.();
      return new Promise<boolean>((resolve) =>
        setPending({ allowance, resolve }),
      );
    },
    [],
  );

  const showRefusal = useCallback((allowance: ExportAllowance) => {
    setPending({ allowance, resolve: () => undefined });
  }, []);

  const close = (go: boolean) => {
    pending?.resolve(go);
    setPending(null);
  };

  const dialog = (
    <ExportShareCapDialog
      allowance={pending?.allowance ?? null}
      onClose={close}
    />
  );
  return { confirm, showRefusal, dialog };
}
